<div class="cp">

  <!-- ── Page Header ───────────────────────────────── -->
  <div class="cp__header">
    <div>
      <h1 class="cp__title">Company Profile</h1>
      <p class="cp__subtitle">Manage your company details and branding.</p>
    </div>
    <button class="cp__edit-btn"
            (click)="toggleEdit()"
            *ngIf="!editMode && profile">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M11 4H4C3.5 4 3 4.4 3 5V20C3 20.6 3.5 21 4 21H19C19.6 21 20 20.6 20 20V13"
              stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <path d="M18.5 2.5C19.3 1.7 20.7 1.7 21.5 2.5C22.3 3.3 22.3 4.7 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z"
              stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
      Edit Profile
    </button>
  </div>

  <!-- ── Success / Error banners ───────────────────── -->
  <div class="cp__banner cp__banner--success" *ngIf="saveSuccess">
    ✓ {{ saveSuccess }}
  </div>
  <div class="cp__banner cp__banner--error" *ngIf="saveError">
    ⚠ {{ saveError }}
  </div>

  <!-- ── Loading skeleton ──────────────────────────── -->
  <div class="cp__skeleton" *ngIf="loadingProfile">
    <div class="cp__skeleton-logo"></div>
    <div class="cp__skeleton-lines">
      <div class="cp__skeleton-line cp__skeleton-line--wide"></div>
      <div class="cp__skeleton-line cp__skeleton-line--mid"></div>
      <div class="cp__skeleton-line cp__skeleton-line--narrow"></div>
    </div>
  </div>

  <!-- ── Profile Card ──────────────────────────────── -->
  <div class="cp__card cp__card--profile" *ngIf="!loadingProfile && profile">

    <!-- Logo section -->
    <div class="cp__logo-section">

      <!-- Logo display -->
      <div class="cp__logo-wrap">
        <img *ngIf="displayLogoUrl"
             [src]="displayLogoUrl"
             [alt]="profile.name + ' logo'"
             class="cp__logo-img"
             (error)="localLogoPreview = null"/>

        <!-- Initials fallback when no logo is set -->
        <div *ngIf="!displayLogoUrl"
             class="cp__logo-initials"
             [style.background]="'#1F3A5F'">
          {{ companyInitials }}
        </div>

        <!-- Upload overlay — shown on hover -->
        <label class="cp__logo-overlay"
               [class.cp__logo-overlay--uploading]="uploadingLogo"
               for="logoInput"
               title="Click to change logo">
          <span *ngIf="!uploadingLogo">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M21 15V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V15"
                    stroke="white" stroke-width="2" stroke-linecap="round"/>
              <polyline points="17 8 12 3 7 8"
                        stroke="white" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round"/>
              <line x1="12" y1="3" x2="12" y2="15"
                    stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span>Upload Logo</span>
          </span>

          <!-- Progress ring during upload -->
          <svg *ngIf="uploadingLogo"
               class="cp__progress-ring"
               width="36" height="36" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="16"
                    fill="none" stroke="rgba(255,255,255,0.3)"
                    stroke-width="3"/>
            <circle cx="18" cy="18" r="16"
                    fill="none" stroke="white"
                    stroke-width="3"
                    stroke-dasharray="100"
                    [attr.stroke-dashoffset]="100 - uploadProgress"
                    stroke-linecap="round"
                    transform="rotate(-90 18 18)"/>
            <text x="18" y="22"
                  text-anchor="middle"
                  font-size="8"
                  fill="white">{{ uploadProgress }}%</text>
          </svg>
        </label>

        <!-- Hidden file input -->
        <input id="logoInput"
               type="file"
               accept="image/jpeg,image/png,image/webp"
               class="cp__logo-input"
               (change)="onLogoSelected($event)"
               [disabled]="uploadingLogo"/>
      </div>

      <!-- Logo messages and hint -->
      <div class="cp__logo-info">
        <p class="cp__logo-hint">JPEG, PNG or WebP · Max 2MB</p>
        <p class="cp__logo-success" *ngIf="logoSuccess">✓ {{ logoSuccess }}</p>
        <p class="cp__logo-error"   *ngIf="logoError">⚠ {{ logoError }}</p>
      </div>
    </div>

    <!-- Company name and meta -->
    <div class="cp__identity">
      <h2 class="cp__company-name">{{ profile.name }}</h2>
      <span class="cp__industry-tag" *ngIf="profile.industry">
        {{ profile.industry }}
      </span>
      <p class="cp__member-since">Member since {{ memberSince }}</p>
    </div>

  </div>

  <!-- ── Stat Cards ─────────────────────────────────── -->
  <div class="cp__stats" *ngIf="!loadingStats && stats">
    <div class="cp__stat">
      <span class="cp__stat-value">{{ stats.totalWorkers }}</span>
      <span class="cp__stat-label">WORKERS</span>
    </div>
    <div class="cp__stat">
      <span class="cp__stat-value">{{ stats.totalJobs }}</span>
      <span class="cp__stat-label">TOTAL JOBS</span>
    </div>
    <div class="cp__stat">
      <span class="cp__stat-value">{{ stats.completedJobs }}</span>
      <span class="cp__stat-label">COMPLETED</span>
    </div>
    <div class="cp__stat">
      <span class="cp__stat-value">{{ stats.totalHoursLogged }}</span>
      <span class="cp__stat-label">HOURS LOGGED</span>
    </div>
    <div class="cp__stat">
      <span class="cp__stat-value">{{ stats.totalWorkLogs }}</span>
      <span class="cp__stat-label">WORK LOGS</span>
    </div>
  </div>

  <!-- ── Company Details (view mode) ───────────────── -->
  <div class="cp__card" *ngIf="!loadingProfile && profile && !editMode">
    <h3 class="cp__card-title">Company Details</h3>
    <div class="cp__fields">
      <div class="cp__field">
        <span class="cp__field-label">COMPANY NAME</span>
        <span class="cp__field-value">{{ profile.name }}</span>
      </div>
      <div class="cp__field">
        <span class="cp__field-label">INDUSTRY</span>
        <span class="cp__field-value">{{ profile.industry || '—' }}</span>
      </div>
      <div class="cp__field">
        <span class="cp__field-label">ADDRESS</span>
        <span class="cp__field-value">{{ profile.address || '—' }}</span>
      </div>
      <div class="cp__field">
        <span class="cp__field-label">PHONE</span>
        <span class="cp__field-value">{{ profile.phone || '—' }}</span>
      </div>
      <div class="cp__field">
        <span class="cp__field-label">EMAIL</span>
        <span class="cp__field-value">{{ profile.email || '—' }}</span>
      </div>
      <div class="cp__field">
        <span class="cp__field-label">WEBSITE</span>
        <span class="cp__field-value">
          <a *ngIf="profile.website"
             [href]="profile.website"
             target="_blank"
             rel="noopener noreferrer"
             class="cp__link">
            {{ profile.website }}
          </a>
          <span *ngIf="!profile.website">—</span>
        </span>
      </div>
      <div class="cp__field cp__field--full" *ngIf="profile.description">
        <span class="cp__field-label">DESCRIPTION</span>
        <span class="cp__field-value">{{ profile.description }}</span>
      </div>
    </div>
  </div>

  <!-- ── Company Details (edit mode) ───────────────── -->
  <div class="cp__card" *ngIf="!loadingProfile && profile && editMode">
    <h3 class="cp__card-title">Edit Company Details</h3>

    <div class="cp__form">
      <div class="cp__form-row">
        <div class="cp__form-group">
          <label class="cp__form-label">Company Name *</label>
          <input class="cp__form-input"
                 type="text"
                 [(ngModel)]="editForm.name"
                 placeholder="Your company name"/>
        </div>
        <div class="cp__form-group">
          <label class="cp__form-label">Industry</label>
          <input class="cp__form-input"
                 type="text"
                 [(ngModel)]="editForm.industry"
                 placeholder="e.g. Oil & Gas, Construction"/>
        </div>
      </div>

      <div class="cp__form-row">
        <div class="cp__form-group">
          <label class="cp__form-label">Phone</label>
          <input class="cp__form-input"
                 type="tel"
                 [(ngModel)]="editForm.phone"
                 placeholder="+234 ..."/>
        </div>
        <div class="cp__form-group">
          <label class="cp__form-label">Email</label>
          <input class="cp__form-input"
                 type="email"
                 [(ngModel)]="editForm.email"
                 placeholder="company@example.com"/>
        </div>
      </div>

      <div class="cp__form-group cp__form-group--full">
        <label class="cp__form-label">Address</label>
        <input class="cp__form-input"
               type="text"
               [(ngModel)]="editForm.address"
               placeholder="Street, City, State"/>
      </div>

      <div class="cp__form-group cp__form-group--full">
        <label class="cp__form-label">Website</label>
        <input class="cp__form-input"
               type="url"
               [(ngModel)]="editForm.website"
               placeholder="https://yourcompany.com"/>
      </div>

      <div class="cp__form-group cp__form-group--full">
        <label class="cp__form-label">Description</label>
        <textarea class="cp__form-input cp__form-textarea"
                  [(ngModel)]="editForm.description"
                  placeholder="Brief description of your company..."
                  rows="4"></textarea>
      </div>

      <div class="cp__form-actions">
        <button class="cp__btn cp__btn--secondary"
                (click)="cancelEdit()"
                [disabled]="saving">
          Cancel
        </button>
        <button class="cp__btn cp__btn--primary"
                (click)="saveProfile()"
                [disabled]="saving || !editForm.name.trim()">
          <span class="cp__btn-spinner" *ngIf="saving"></span>
          {{ saving ? 'Saving…' : 'Save Changes' }}
        </button>
      </div>
    </div>
  </div>

</div>